import { z } from 'zod'

/**
 * Umgebungen und Releases (KONTOR-PHASE-9.md).
 *
 * Ein Projekt hatte bisher einen Ort und einen Vorgang, der vorwärts läuft. Hier kommen zwei
 * Begriffe dazu: die **Umgebung** (wo etwas läuft) und das **Release** (was dort läuft, aus
 * welchem Commit, seit wann, und worauf zurückgerollt würde).
 */
export const environmentKindSchema = z.enum(['prod', 'preview'])
export type EnvironmentKind = z.infer<typeof environmentKindSchema>

export const environmentSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  kind: environmentKindSchema,
  serverId: z.string(),
  basePath: z.string(),
  pm2Name: z.string(),
  url: z.string(),
  healthPath: z.string(),
  branch: z.string(),
  dbName: z.string().nullable(),
  /** Produktion: strengere Bestätigungen, und der Projektname muss abgetippt werden (§4.2). */
  isProtected: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
})
export type Environment = z.infer<typeof environmentSchema>

const environmentFields = {
  kind: environmentKindSchema,
  serverId: z.string().min(1, 'Bitte wähle einen Server.'),
  basePath: z
    .string()
    .trim()
    .min(1, 'Das Basisverzeichnis fehlt.')
    .refine((value) => value.startsWith('/'), 'Das Basisverzeichnis muss absolut sein.'),
  pm2Name: z.string().trim().min(1, 'Der Name des Prozesses fehlt.'),
  url: z
    .string()
    .trim()
    .min(1, 'Die Adresse fehlt.')
    .refine(
      (value) => value.startsWith('https://') || value.startsWith('http://'),
      'Die Adresse braucht http:// oder https://.',
    ),
  healthPath: z
    .string()
    .trim()
    .refine((value) => value.startsWith('/'), 'Der Health-Pfad beginnt mit einem Schrägstrich.'),
  branch: z.string().trim().min(1, 'Der Branch fehlt.'),
  dbName: z.string().trim().max(120).nullable(),
  isProtected: z.boolean(),
}

export const createEnvironmentSchema = z.object({
  ...environmentFields,
  healthPath: environmentFields.healthPath.default('/api/health'),
  dbName: environmentFields.dbName.default(null),
  isProtected: environmentFields.isProtected.default(false),
})
export type CreateEnvironmentInput = z.input<typeof createEnvironmentSchema>

export const updateEnvironmentSchema = z
  .object(environmentFields)
  .omit({ kind: true })
  .partial()
  .refine((value) => Object.keys(value).length > 0, 'Es wurde nichts geändert.')

export const releaseStatusSchema = z.enum([
  'building',
  'ready',
  'promoting',
  'live',
  'superseded',
  'failed',
  'rolled_back',
])
export type ReleaseStatus = z.infer<typeof releaseStatusSchema>

export const releaseSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  commitSha: z.string(),
  commitSubject: z.string(),
  commitAuthor: z.string().nullable(),
  commitAt: z.iso.datetime().nullable(),
  releaseDir: z.string().nullable(),
  status: releaseStatusSchema,
  builtAt: z.iso.datetime().nullable(),
  promotedAt: z.iso.datetime().nullable(),
  supersededAt: z.iso.datetime().nullable(),
  rolledBackAt: z.iso.datetime().nullable(),
  rollbackReason: z.enum(['manual', 'healthcheck_failed', 'step_failed']).nullable(),
  backupRunId: z.string().nullable(),
  previousRelease: z.string().nullable(),
  createdAt: z.iso.datetime(),
})
export type Release = z.infer<typeof releaseSchema>

/** Ein Commit, wie `git log` ihn ausgibt — für die Liste „was ginge raus". */
export const commitSchema = z.object({
  sha: z.string(),
  shortSha: z.string(),
  subject: z.string(),
  author: z.string(),
  at: z.iso.datetime().nullable(),
})
export type Commit = z.infer<typeof commitSchema>

export const healthStateSchema = z.enum(['ok', 'failing', 'unknown'])
export type HealthState = z.infer<typeof healthStateSchema>

/**
 * Was wirklich auf dem Server steht — gelesen, nicht aus der Datenbank behauptet.
 *
 * §9 der Phasendatei ist hier unmissverständlich: Der Zustand auf dem Server ist die Wahrheit.
 * Weicht er von dem ab, was Kontor gespeichert hat, wird das angezeigt und nicht stillschweigend
 * überschrieben — sonst zeigt die Oberfläche nach einem Absturz mitten im Deploy eine veraltete
 * Behauptung an, und das ist genau der Moment, in dem man sie glaubt.
 */
export const environmentStateSchema = z.object({
  environmentId: z.string(),
  kind: environmentKindSchema,
  /** Wohin der `current`-Symlink zeigt; null, wenn es ihn nicht gibt. */
  currentRelease: z.string().nullable(),
  commitSha: z.string().nullable(),
  commitSubject: z.string().nullable(),
  /** Läuft der PM2-Prozess, und seit wann. */
  processOnline: z.boolean().nullable(),
  processUptimeSec: z.number().nonnegative().nullable(),
  processRestarts: z.number().int().nonnegative().nullable(),
  health: healthStateSchema,
  healthDetail: z.string().nullable(),
  healthMs: z.number().nonnegative().nullable(),
  /** Seit wann dieser Stand läuft, aus dem Release oder ersatzweise der Prozesslaufzeit. */
  liveSince: z.iso.datetime().nullable(),
  /** Was nicht gelesen werden konnte — damit die Seite nie etwas Leeres behauptet. */
  notes: z.array(z.string()),
})
export type EnvironmentState = z.infer<typeof environmentStateSchema>

export const deployOverviewSchema = z.object({
  projectId: z.string(),
  checkedAt: z.iso.datetime(),
  environments: z.array(
    z.object({ environment: environmentSchema, state: environmentStateSchema.nullable() }),
  ),
  /**
   * Wie viele Commits die Preview vor Live liegt, plus die Liste. Null, wenn sich der Abstand
   * nicht bestimmen ließ (fehlende Umgebung, unerreichbarer Server, kein Git).
   */
  ahead: z.number().int().nonnegative().nullable(),
  /** Negativ wäre „Live ist vor Preview" — kommt vor, wenn jemand direkt auf Live gearbeitet hat. */
  behind: z.number().int().nonnegative().nullable(),
  pendingCommits: z.array(commitSchema),
  /** Weicht der gelesene Zustand von der Datenbank ab, steht hier, was nicht zusammenpasst. */
  drift: z.array(z.string()),
  notes: z.array(z.string()),
})
export type DeployOverview = z.infer<typeof deployOverviewSchema>
